import { documentTypesApi } from 'app/services/api';
import { normalize, schema } from 'normalizr';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { getDocumentTypeById } from 'store/system/selectors';

import { GET_DOCUMENT_EXTRACTION_FIELDS_TYPE } from './documentExtractionFields.actions';

export const DOCUMENT_TYPES_ACTION_TYPE = createNamedActions('documentTypes', {
  GET_DOCUMENT_TYPES: 'GET_DOCUMENT_TYPES',
  GET_DOCUMENT_TYPE: 'GET_DOCUMENT_TYPE',
  CREATE_DOCUMENT_TYPE: 'CREATE_DOCUMENT_TYPE',
  EDIT_DOCUMENT_TYPE: 'EDIT_DOCUMENT_TYPE',
  REORDER_DOCUMENT_TYPE: 'REORDER_DOCUMENT_TYPE',
  DELETE_DOCUMENT_TYPE: 'DELETE_DOCUMENT_TYPE',
});

const documentExtractionFieldSchema = new schema.Entity(
  'extractionFields',
  {},
  {
    idAttribute: 'id',
    processStrategy: entity => {
      entity.documentFieldTypeId = entity.documentFieldType.id;

      return entity;
    },
  },
);

const extractionFields = new schema.Array(documentExtractionFieldSchema);

documentExtractionFieldSchema.define({ children: extractionFields });

const documentTypeSchema = new schema.Entity(
  'data',
  {
    extractionSettings: {
      extractionFields,
    },
  },
  {
    idAttribute: 'id',
    processStrategy: ({ categoryId, id, name, metadata, ...restData }) => ({
      ...metadata,
      ...restData,
      categoryId,
      id,
      documentTypeId: id,
      documentTypeName: name,
      isInactive: !categoryId,
    }),
  },
);

export const getDocumentTypesAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentTypesApi.getDocumentTypes(params);

  return dispatch({
    type: DOCUMENT_TYPES_ACTION_TYPE.GET_DOCUMENT_TYPES,
    payload: normalize(data, [documentTypeSchema]).entities.data,
  });
});

export const getDocumentTypeAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentTypesApi.getDocumentTypeById(params);

  const normalizedData = normalize(data, documentTypeSchema).entities;
  const documentType = normalizedData.data[data.id];

  if (params?.include?.includes('extractionFields')) {
    const { extractionFields } = normalizedData;

    dispatch({
      type: GET_DOCUMENT_EXTRACTION_FIELDS_TYPE,
      payload: { documentExtractionFields: extractionFields },
    });
  }

  return dispatch({
    type: DOCUMENT_TYPES_ACTION_TYPE.GET_DOCUMENT_TYPE,
    payload: documentType,
  });
});

export const addDocumentTypeAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentTypesApi.createDocumentTypes(params);

  return dispatch({
    type: DOCUMENT_TYPES_ACTION_TYPE.CREATE_DOCUMENT_TYPE,
    payload: normalize(data, documentTypeSchema).entities.data[data.id],
  });
});

export const editDocumentTypeAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentTypesApi.editDocumentType(params);

  return dispatch({
    type: DOCUMENT_TYPES_ACTION_TYPE.EDIT_DOCUMENT_TYPE,
    payload: normalize(data, documentTypeSchema).entities.data[data.id],
  });
});

export const deleteDocumentTypeAction = catchAsyncActionError(params => async dispatch => {
  await documentTypesApi.deleteDocumentType(params);

  return dispatch({
    type: DOCUMENT_TYPES_ACTION_TYPE.DELETE_DOCUMENT_TYPE,
    meta: params,
  });
});

export const reorderDocumentTypeAction = catchAsyncActionError(params => async dispatch => {
  await documentTypesApi.reorderDocumentType(params);

  return dispatch({ type: DOCUMENT_TYPES_ACTION_TYPE.REORDER_DOCUMENT_TYPE });
});

export const setDocumentTypeSystemKeyAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentTypesApi.setDocumentTypeSystemKey(params);

  return dispatch({
    type: DOCUMENT_TYPES_ACTION_TYPE.EDIT_DOCUMENT_TYPE,
    payload: normalize(data, documentTypeSchema).entities.data[data.id],
  });
});

export const setDocumentTypeClassificationTierAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await documentTypesApi.setDocumentTypeClassificationTier(params);

    return dispatch({
      type: DOCUMENT_TYPES_ACTION_TYPE.EDIT_DOCUMENT_TYPE,
      payload: normalize(data, documentTypeSchema).entities.data[data.id],
    });
  },
);

export const setDocumentTypeContinuationHintAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await documentTypesApi.setDocumentTypeContinuationHint(params);

    return dispatch({
      type: DOCUMENT_TYPES_ACTION_TYPE.EDIT_DOCUMENT_TYPE,
      payload: normalize(data, documentTypeSchema).entities.data[data.id],
    });
  },
);

export const moveDocumentTypeWithSystemCategoryAction = params => (dispatch, getState) => {
  const state = getState();

  const documentType = getDocumentTypeById(params.documentTypeId)(state);

  const documentCategoryId = params.isRollbackToPrevCategory
    ? documentType.previousDocumentCategoryId
    : params.toCategoryId || documentType.categoryId;

  return dispatch(
    editDocumentTypeAction({
      id: params.documentTypeId,
      name: documentType.documentTypeName,
      isExcludedFromDataExtraction: params.isExcludedFromDataExtraction,
      documentCategoryId: params.isInactive ? null : documentCategoryId,
    }),
  );
};
